/**
 * Motor de sincronización offline → online
 */

import { offlineStorage, type QueuedOperation } from './offline-storage';
import { supabase } from './supabase';
import toast from 'react-hot-toast';

// Specialized sync types for technician operations
type SyncType = 'inspection' | 'photo' | 'insumo' | 'cita_status';

// Type for internal sync items
export interface SyncQueueItem {
  type: SyncType;
  id: string;
  data: SyncItemData;
  timestamp: number;
  retries: number;
}

// Helper to check if operation is a specialized sync item
function isSpecializedSyncType(type: string): type is SyncType {
  return ['inspection', 'photo', 'insumo', 'cita_status'].includes(type);
}

// Tipos específicos para cada tipo de sync
export interface InspectionSyncData {
  citaId: string;
  areaInspeccionada: string;
  plagaEncontrada?: string;
  nivelInfestacion?: string;
  observaciones?: string;
  fotos?: string[];
}

export interface PhotoSyncData {
  citaId: string;
  filePath: string;
  dataUrl: string;
  filename: string;
}

export interface InsumoSyncData {
  citaId: string;
  productoNombre: string;
  unidad: string;
  cantidadUsada: number;
  costoUnitario?: number;
  productoId?: string;
}

export interface CitaStatusSyncData {
  citaId: string;
  estado: string;
  horaRealInicio?: string;
  horaRealFin?: string;
  notas?: string;
}

export type SyncItemData = InspectionSyncData | PhotoSyncData | InsumoSyncData | CitaStatusSyncData;

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: Array<{ id: string; error: string }>;
}

const MAX_RETRIES = 3;
const _RETRY_DELAY = 2000; // Reserved for future retry logic

export const syncEngine = {
  /**
   * Sincronizar toda la cola
   */
  async syncAll(): Promise<SyncResult> {
    const queue = await offlineStorage.getSyncQueue();
    
    if (queue.length === 0) {
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    if (import.meta.env.DEV) console.log(`🔄 Iniciando sincronización de ${queue.length} items...`);

    let synced = 0;
    let failed = 0;
    const errors: Array<{ id: string; error: string }> = [];

    for (const item of queue) {
      try {
        // Check if it's a specialized sync type or generic CRUD
        if (isSpecializedSyncType(item.type)) {
          const syncItem: SyncQueueItem = {
            type: item.type,
            id: item.id,
            data: item.data as SyncItemData,
            timestamp: item.timestamp,
            retries: item.retries,
          };
          await this.syncItem(syncItem);
        } else {
          // Handle generic CRUD operations
          await this.syncGenericOperation(item);
        }
        await offlineStorage.removeFromSyncQueue(item.id);
        synced++;
        if (import.meta.env.DEV) console.log(`✅ Sincronizado: ${item.type} ${item.id}`);
      } catch (error) {
        failed++;
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        errors.push({ id: item.id, error: errorMessage });
        console.error(`❌ Error sincronizando ${item.type} ${item.id}:`, errorMessage);

        // Incrementar reintentos
        if (item.retries < MAX_RETRIES) {
          item.retries++;
          // Mantener en cola para reintentar
        } else {
          // Máximo de reintentos alcanzado, remover de cola
          await offlineStorage.removeFromSyncQueue(item.id);
          console.warn(`⚠️ Máximo de reintentos alcanzado para ${item.id}`);
        }
      }
    }

    const result: SyncResult = {
      success: failed === 0,
      synced,
      failed,
      errors,
    };

    if (synced > 0) {
      toast.success(`✅ ${synced} items sincronizados`);
    }

    if (failed > 0) {
      toast.error(`❌ ${failed} items fallaron`);
    }

    return result;
  },

  /**
   * Sincronizar un item individual
   */
  async syncItem(item: SyncQueueItem): Promise<void> {
    switch (item.type) {
      case 'inspection':
        await this.syncInspection(item);
        break;
      case 'photo':
        await this.syncPhoto(item);
        break;
      case 'insumo':
        await this.syncInsumo(item);
        break;
      case 'cita_status':
        await this.syncCitaStatus(item);
        break;
      default:
        throw new Error(`Tipo de sincronización desconocido: ${item.type}`);
    }
  },

  /**
   * Sincronizar inspección
   * Note: Field mapping from mobile camelCase to database snake_case
   */
  async syncInspection(item: SyncQueueItem): Promise<void> {
    const data = item.data as InspectionSyncData & Record<string, unknown>;

    // Map mobile fields to database schema
    const insertData = {
      cita_id: data.citaId,
      tecnico_id: data.tecnicoId,
      tipo_plaga: data.tipoPlaga,
      nivel_severidad: data.nivelSeveridad,
      observaciones: data.observaciones,
      tratamiento_aplicado: data.tratamientoAplicado,
      producto_usado: data.productoUsado,
      dosis_ml: data.dosisMl,
      areas_tratadas: data.areasTratadas,
      foto_urls: data.fotoUrls ?? [],
      firma_cliente_url: data.firmaClienteUrl,
      coordenadas: data.coordenadas,
      timestamp_sincronizacion: new Date().toISOString(),
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('inspecciones')
      .insert(insertData);

    if (error) throw new Error(error.message);

    // Remover de storage offline
    await offlineStorage.removeInspection(item.id);
  },

  /**
   * Sincronizar foto
   */
  async syncPhoto(item: SyncQueueItem): Promise<void> {
    const photoData = await offlineStorage.getPhoto(item.id);
    
    if (!photoData) {
      throw new Error('Foto no encontrada en storage offline');
    }

    // Convertir dataUrl a Blob
    const blob = dataURLToBlob(photoData.dataUrl);

    // Subir a Supabase Storage
    const fileName = `${item.id}_${Date.now()}.jpg`;
    const { data, error } = await supabase.storage
      .from('inspecciones')
      .upload(fileName, blob, {
        contentType: 'image/jpeg',
        cacheControl: '3600',
      });

    if (error) throw new Error(error.message);

    // Obtener URL pública
    const { data: urlData } = supabase.storage
      .from('inspecciones')
      .getPublicUrl(data.path);

    if (import.meta.env.DEV) console.log(`📸 Foto subida: ${urlData.publicUrl}`);

    // Remover de storage offline
    await offlineStorage.removePhoto(item.id);
  },

  /**
   * Sincronizar insumo
   */
  async syncInsumo(item: SyncQueueItem): Promise<void> {
    const data = item.data as InsumoSyncData;

    const { error } = await supabase
      .from('insumos_consumidos')
      .insert({
        cita_id: data.citaId,
        producto_nombre: data.productoNombre,
        unidad: data.unidad,
        cantidad_usada: data.cantidadUsada,
        costo_unitario: data.costoUnitario ?? 0,
        ...(data.productoId ? { producto_id: data.productoId } : {}),
      });

    if (error) throw new Error(error.message);
  },

  /**
   * Sincronizar cambio de estado de cita
   */
  async syncCitaStatus(item: SyncQueueItem): Promise<void> {
    const data = item.data as CitaStatusSyncData & Record<string, unknown>;

    const { error } = await supabase
      .from('citas')
      .update({
        estado: data.estado,
        hora_inicio_real: data.horaInicioReal,
        hora_fin_real: data.horaFinReal,
      })
      .eq('id', data.citaId);

    if (error) throw new Error(error.message);
  },

  /**
   * Sincronizar operaciones CRUD genéricas
   * Note: Uses type assertions for dynamic table names - this is necessary for offline sync flexibility
   */
  async syncGenericOperation(item: QueuedOperation): Promise<void> {
    const { type, entity, data } = item;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const supabaseAny = supabase as any;
    
    switch (type) {
      case 'create': {
        const { error: createError } = await supabaseAny.from(entity).insert(data);
        if (createError) throw new Error(createError.message);
        break;
      }
      case 'update': {
        const { error: updateError } = await supabaseAny
          .from(entity)
          .update(data)
          .eq('id', (data as { id: string }).id);
        if (updateError) throw new Error(updateError.message);
        break;
      }
      case 'delete': {
        const { error: deleteError } = await supabaseAny
          .from(entity)
          .delete()
          .eq('id', (data as { id: string }).id);
        if (deleteError) throw new Error(deleteError.message);
        break;
      }
    }
  },

  /**
   * Verificar si hay items pendientes de sincronización
   */
  async hasPendingSync(): Promise<boolean> {
    const queue = await offlineStorage.getSyncQueue();
    return queue.length > 0;
  },

  /**
   * Obtener cantidad de items pendientes
   */
  async getPendingCount(): Promise<number> {
    const queue = await offlineStorage.getSyncQueue();
    return queue.length;
  },

  /**
   * Limpiar cola de sincronización (usar con precaución)
   */
  async clearQueue(): Promise<void> {
    await offlineStorage.clearSyncQueue();
    if (import.meta.env.DEV) console.log('🗑️ Cola de sincronización limpiada');
  },
};

// Helper: Convertir dataURL a Blob
function dataURLToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0]?.match(/:(.*?);/);
  const mime = mimeMatch?.[1] ?? 'image/jpeg';
  const base64Data = arr[1];
  if (!base64Data) {
    throw new Error('Invalid data URL format');
  }
  const bstr = atob(base64Data);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
}

/**
 * Hook para sincronización automática
 */
export function setupAutoSync() {
  // Escuchar evento de reconexión
  window.addEventListener('online-sync', async () => {
    if (import.meta.env.DEV) console.log('🌐 Conectado - iniciando sincronización automática...');
    
    try {
      const result = await syncEngine.syncAll();
      
      if (import.meta.env.DEV) {
        if (result.success) {
          console.log(`✅ Sincronización completa: ${result.synced} items`);
        } else {
          console.warn(`⚠️ Sincronización con errores: ${result.failed} fallaron`);
        }
      }
    } catch (error) {
      console.error('❌ Error en sincronización automática:', error);
    }
  });

  // Sincronizar periódicamente si está online
  const syncInterval = setInterval(async () => {
    if (navigator.onLine) {
      const hasPending = await syncEngine.hasPendingSync();
      
      if (hasPending) {
        if (import.meta.env.DEV) console.log('⏰ Sincronización periódica iniciada...');
        await syncEngine.syncAll();
      }
    }
  }, 60000); // Cada minuto

  // Cleanup
  return () => {
    clearInterval(syncInterval);
    window.removeEventListener('online-sync', () => {});
  };
}

export default syncEngine;
